// v1.28.12 — Reconciliation Statement (Party Login only)
// Shows the signed-in party's settled ledger + pending slips in one
// timeline with Dr/Cr indicators, filterable by date range, downloadable
// as PDF. Backend enforces party_id isolation in /api/ledger/{party_id}
// and /api/export/ledger/{party_id}.pdf.
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DownloadSimple, ArrowClockwise, ArrowsCounterClockwise, Info, MicrosoftExcelLogo, EnvelopeSimple, Check, Broom } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));
const formatDate = (iso) => {
    if (!iso) return "—";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
};

const modeLabel = (r) => {
    if (r.payment_mode) return r.payment_mode;
    if (r.txn_type === "invoice") return "Invoice";
    if (r.txn_type === "commission") return "Commission";
    if (r.txn_type === "portal_fee") return "Portal Fee";
    if (r.txn_type === "adjustment") return "Adjustment";
    if (r.txn_type === "payout") return "Payout";
    if (r.txn_type === "payment") return "Payment";
    return "—";
};

const rowStatus = (r) => {
    // Ledger rows come from settled transactions → "Approved"/"Settled";
    // Pending slips are surfaced separately below and merged in the UI.
    if (r._pending) return { label: "Pending", cls: "bg-amber-100 text-amber-800" };
    if (r.txn_type === "payment") return { label: "Approved", cls: "bg-emerald-100 text-emerald-800" };
    if (r.txn_type === "payout") return { label: "Settled",  cls: "bg-sky-100 text-sky-800" };
    return { label: "Settled", cls: "bg-slate-100 text-slate-700" };
};

const drCr = (signed) => {
    const v = Number(signed || 0);
    if (v > 0) return { tag: "Dr", cls: "bg-rose-100 text-rose-700",    hint: "Party owes MD Impex" };
    if (v < 0) return { tag: "Cr", cls: "bg-emerald-100 text-emerald-700", hint: "MD Impex owes party" };
    return { tag: "—", cls: "bg-slate-100 text-slate-500", hint: "" };
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const firstOfMonthIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function ReconciliationStatement() {
    const { user } = useAuth();
    const pid = user?.party_id;
    const [dateFrom, setDateFrom] = useState(firstOfMonthIso());
    const [dateTo, setDateTo] = useState(todayIso());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingXlsx, setDownloadingXlsx] = useState(false);
    const [emailing, setEmailing] = useState(false);

    // v1.28.14 — Party-side "matched in my books" ticks. Stored per-party in
    // localStorage so ticks persist across sessions. Client-only — never
    // sent to the backend, never visible to admin. Row goes green when ticked.
    const matchedStorageKey = pid ? `recon_matched:${pid}` : "";
    const [matched, setMatched] = useState(() => new Set());
    useEffect(() => {
        if (!matchedStorageKey) return;
        try {
            const raw = localStorage.getItem(matchedStorageKey);
            if (raw) setMatched(new Set(JSON.parse(raw)));
            else setMatched(new Set());
        } catch { setMatched(new Set()); }
    }, [matchedStorageKey]);

    const persistMatched = (nextSet) => {
        if (!matchedStorageKey) return;
        try {
            localStorage.setItem(matchedStorageKey, JSON.stringify([...nextSet]));
        } catch { /* localStorage quota / private-mode → silent */ }
    };

    const toggleMatched = (rowId) => {
        setMatched((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            persistMatched(next);
            return next;
        });
    };

    const clearAllMatched = () => {
        if (matched.size === 0) return;
        if (!window.confirm(`Clear all ${matched.size} row ticks for this party?\n\nThese ticks are stored only on this device — they don't affect the admin's reports.`)) return;
        const empty = new Set();
        setMatched(empty);
        persistMatched(empty);
    };

    const load = async () => {
        if (!pid) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            const { data: d } = await api.get(`/ledger/${pid}?${params.toString()}`);
            setData(d);
        } catch (err) {
            toast.error(err?.message || "Failed to load reconciliation");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pid]);

    // Merge settled ledger rows with pending uploads so the party sees ONE
    // reconciliation timeline. Pending rows are tagged so they never affect
    // Total Paid / Total Due until admin approves them.
    const merged = useMemo(() => {
        if (!data) return [];
        const settled = (data.rows || []).map((r) => ({ ...r, _pending: false }));
        const pending = (data.pending_uploads || [])
            .filter((u) => {
                const d = u.payment_date || "";
                if (dateFrom && d < dateFrom) return false;
                if (dateTo && d > dateTo) return false;
                return true;
            })
            .map((u) => ({
                id: `pending-${u.id}`,
                date: u.payment_date || u.uploaded_at?.slice(0, 10),
                utr: u.utr || "",
                amount: u.amount || 0,
                amount_signed: -Math.abs(Number(u.amount || 0)), // pending payment is a would-be Cr, but shown as informational only
                payment_mode: u.payment_app || "UPI",
                txn_type: "payment",
                description: u.notes || "",
                _pending: true,
                _running_hidden: true,
            }));
        // Sort by date desc then by uploaded_at desc as tie-breaker
        return [...settled, ...pending].sort((a, b) => {
            const da = a.date || ""; const dbb = b.date || "";
            if (da !== dbb) return dbb.localeCompare(da);
            return 0;
        });
    }, [data, dateFrom, dateTo]);

    const totalPaid = useMemo(() => {
        // Sum of credit rows (payments customer made and admin approved)
        return (data?.rows || [])
            .filter((r) => Number(r.amount_signed || 0) < 0)
            .reduce((s, r) => s + Math.abs(Number(r.amount_signed || 0)), 0);
    }, [data]);

    const totalDue = useMemo(() => {
        // Sum of debit rows in-range (invoices / commissions / portal fees)
        return (data?.rows || [])
            .filter((r) => Number(r.amount_signed || 0) > 0)
            .reduce((s, r) => s + Number(r.amount_signed || 0), 0);
    }, [data]);

    const downloadPdf = async () => {
        if (!pid) return;
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            const res = await api.get(`/export/ledger/${pid}.pdf?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const filename = withDatePrefix(`reconciliation-${(data?.party?.name || "party").replace(/\s+/g, "_")}-${dateFrom}_to_${dateTo}.pdf`);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.message || "Failed to download PDF");
        } finally {
            setDownloading(false);
        }
    };

    // v1.28.13 — Excel export uses the existing party-scoped
    // /api/export/ledger/{party_id}.xlsx endpoint. Same date range as PDF.
    const downloadXlsx = async () => {
        if (!pid) return;
        setDownloadingXlsx(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            const res = await api.get(`/export/ledger/${pid}.xlsx?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const filename = withDatePrefix(`reconciliation-${(data?.party?.name || "party").replace(/\s+/g, "_")}-${dateFrom}_to_${dateTo}.xlsx`);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.message || "Failed to download Excel");
        } finally {
            setDownloadingXlsx(false);
        }
    };

    // v1.28.13 — "Email me a copy". Hits the new party-scoped
    // /api/portal/reconciliation/email endpoint which mails the PDF
    // to the party's registered email (or the address they type in).
    const emailMe = async () => {
        if (!pid) return;
        const defaultTo = (data?.party?.email || user?.email || "").trim();
        const to = window.prompt(
            defaultTo
                ? `Email a copy of this reconciliation statement to:\n\n(Leave the address to send to your registered email, or edit to send elsewhere.)`
                : "Enter the email address to send this reconciliation statement to:",
            defaultTo,
        );
        if (to === null) return; // cancelled
        setEmailing(true);
        try {
            const { data: r } = await api.post("/portal/reconciliation/email", { to: (to || "").trim(), format: "pdf" });
            toast.success(`Reconciliation statement emailed to ${r.sent_to}. Check your inbox in a minute.`);
        } catch (err) {
            toast.error(err?.message || "Failed to email the statement");
        } finally {
            setEmailing(false);
        }
    };

    const closingBalance = Number(data?.closing_balance || 0);
    const openingBalance = Number(data?.opening_balance || 0);
    const netBalance = closingBalance; // running balance at end-of-range
    const netTag = drCr(netBalance);

    if (!pid) {
        return (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="reconciliation-no-party">
                No party is linked to your account. Please contact MD Impex support.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="reconciliation-statement">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">Reconciliation Statement</div>
                        <div className="mt-1 text-lg font-bold text-slate-900">{data?.party?.name || "—"}</div>
                        <div className="text-xs text-slate-500">All your entries in one place · Dr = you owe · Cr = MD Impex owes you</div>
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</label>
                            <input
                                type="date"
                                data-testid="reconciliation-date-from"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</label>
                            <input
                                type="date"
                                data-testid="reconciliation-date-to"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
                            />
                        </div>
                        <button
                            type="button"
                            data-testid="reconciliation-apply-btn"
                            onClick={load}
                            disabled={loading}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                        >
                            <ArrowClockwise size={13} weight="bold" /> {loading ? "Loading…" : "Apply"}
                        </button>
                        <button
                            type="button"
                            data-testid="reconciliation-download-pdf-btn"
                            onClick={downloadPdf}
                            disabled={downloading || loading || !data}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-emerald-700 px-3 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
                        >
                            <DownloadSimple size={13} weight="bold" /> {downloading ? "Preparing…" : "Download PDF"}
                        </button>
                        <button
                            type="button"
                            data-testid="reconciliation-download-xlsx-btn"
                            onClick={downloadXlsx}
                            disabled={downloadingXlsx || loading || !data}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-teal-700 px-3 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                        >
                            <MicrosoftExcelLogo size={13} weight="bold" /> {downloadingXlsx ? "Preparing…" : "Download Excel"}
                        </button>
                        <button
                            type="button"
                            data-testid="reconciliation-email-me-btn"
                            onClick={emailMe}
                            disabled={emailing || loading || !data}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-indigo-700 px-3 text-xs font-semibold text-white hover:bg-indigo-800 disabled:opacity-50"
                        >
                            <EnvelopeSimple size={13} weight="bold" /> {emailing ? "Sending…" : "Email me a copy"}
                        </button>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="reconciliation-balance-cards">
                    <BalanceCard label="Opening Balance" value={openingBalance} testId="opening-balance" />
                    <BalanceCard label="Total Paid (Cr)" value={totalPaid} tone="emerald" testId="total-paid" />
                    <BalanceCard label="Total Due (Dr)"  value={totalDue}  tone="rose"    testId="total-due" />
                    <BalanceCard label="Net Balance"     value={Math.abs(netBalance)} tone={netTag.tag === "Cr" ? "emerald" : netTag.tag === "Dr" ? "rose" : "slate"} suffix={netTag.tag} testId="net-balance" />
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px]" data-testid="reconciliation-matched-header">
                    <div className="text-slate-600">
                        <span className="font-bold text-emerald-700">{matched.size}</span>
                        <span className="mx-1">of</span>
                        <span className="font-semibold text-slate-800">{merged.length}</span>
                        <span className="ml-1">matched in your books</span>
                        <span className="ml-2 hidden text-[10px] text-slate-400 sm:inline">(saved on this device only · admin does not see this)</span>
                    </div>
                    {matched.size > 0 && (
                        <button
                            type="button"
                            data-testid="reconciliation-clear-ticks-btn"
                            onClick={clearAllMatched}
                            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
                        >
                            <Broom size={11} weight="bold" /> Clear my ticks
                        </button>
                    )}
                </div>
                <table className="min-w-full text-xs" data-testid="reconciliation-table">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="sticky left-0 z-20 w-[44px] min-w-[44px] bg-slate-50 px-2 py-2 text-center font-bold uppercase tracking-wider text-slate-600" title="Tick to mark this row as matched in your books">
                                <Check size={12} weight="bold" className="mx-auto" />
                            </th>
                            <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-600">Date</th>
                            <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-600">UTR</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Amount</th>
                            <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-600">Payment Mode</th>
                            <th className="px-3 py-2 text-center font-bold uppercase tracking-wider text-slate-600">Dr / Cr</th>
                            <th className="px-3 py-2 text-center font-bold uppercase tracking-wider text-slate-600">Status</th>
                            <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-600">Remarks</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Running Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-t border-slate-100 bg-slate-50/50">
                            <td className="sticky left-0 z-10 w-[44px] min-w-[44px] bg-slate-50/50 px-2 py-2" aria-hidden="true"></td>
                            <td colSpan={7} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Opening Balance</td>
                            <td className="px-3 py-2 text-right font-bold text-slate-800" data-testid="row-opening">{inr(openingBalance)}</td>
                        </tr>
                        {merged.length === 0 && !loading && (
                            <tr>
                                <td colSpan={9} className="px-3 py-8 text-center text-slate-500">No transactions in this date range.</td>
                            </tr>
                        )}
                        {merged.map((r) => {
                            const st = rowStatus(r);
                            const dc = drCr(r.amount_signed);
                            const remarks = r._pending ? "Awaiting admin verification" : (r.description || r.reference || r.notes || "");
                            const isMatched = matched.has(r.id);
                            const rowCls = isMatched
                                ? "bg-emerald-100 hover:bg-emerald-200"
                                : r._pending ? "bg-amber-50/40" : "";
                            const stickyBg = isMatched ? "bg-emerald-100" : (r._pending ? "bg-amber-50" : "bg-white");
                            return (
                                <tr key={r.id} data-testid={`recon-row-${r.id}`} data-matched={isMatched ? "1" : "0"} className={`border-t border-slate-100 ${rowCls}`}>
                                    <td className={`sticky left-0 z-10 w-[44px] min-w-[44px] px-2 py-2 text-center ${stickyBg}`}>
                                        <label className="flex h-8 w-8 cursor-pointer items-center justify-center rounded hover:bg-slate-100 active:bg-slate-200 mx-auto" title={isMatched ? "Untick — mark as not-yet-matched" : "Tick — I have this in my books"}>
                                            <input
                                                type="checkbox"
                                                data-testid={`recon-match-${r.id}`}
                                                checked={isMatched}
                                                onChange={() => toggleMatched(r.id)}
                                                className="h-5 w-5 cursor-pointer rounded border-slate-400 text-emerald-600 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-0"
                                                aria-label={`Mark row ${r.utr || r.id} as matched`}
                                            />
                                        </label>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatDate(r.date)}</td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{r.utr || "—"}</td>
                                    <td className="px-3 py-2 text-right font-medium text-slate-900">{inr(r.amount)}</td>
                                    <td className="px-3 py-2 text-slate-700">{modeLabel(r)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${dc.cls}`} title={dc.hint}>{dc.tag}</span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{remarks || "—"}</td>
                                    <td className="px-3 py-2 text-right font-medium text-slate-800">
                                        {r._running_hidden ? "—" : inr(r.running_balance)}
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="border-t-2 border-slate-300 bg-slate-100">
                            <td className="sticky left-0 z-10 w-[44px] min-w-[44px] bg-slate-100 px-2 py-2" aria-hidden="true"></td>
                            <td colSpan={7} className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-700">Closing Balance</td>
                            <td className={`px-3 py-2 text-right font-black ${netTag.tag === "Dr" ? "text-rose-700" : netTag.tag === "Cr" ? "text-emerald-700" : "text-slate-900"}`} data-testid="row-closing">
                                {inr(Math.abs(closingBalance))} {netTag.tag !== "—" && <span className="ml-1 text-[10px] font-bold">{netTag.tag}</span>}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600" data-testid="reconciliation-legend">
                <div className="flex items-start gap-2">
                    <Info size={14} weight="fill" className="mt-0.5 shrink-0 text-slate-500" />
                    <div>
                        <b>Tick</b> a row after you've matched it in your books — it turns green and stays ticked on this device (not sent to admin). <b>Dr (Debit)</b> means you owe MD Impex. <b>Cr (Credit)</b> means MD Impex owes you. <b>Pending</b> rows are slips you uploaded that admin has not verified yet — they do not affect Total Paid / Total Due until approved.
                    </div>
                </div>
            </div>
        </div>
    );
}

function BalanceCard({ label, value, tone = "slate", suffix, testId }) {
    const toneCls =
        tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-800" :
        tone === "rose"    ? "border-rose-200 bg-rose-50 text-rose-800" :
        "border-slate-200 bg-white text-slate-800";
    return (
        <div className={`rounded-md border p-2.5 ${toneCls}`} data-testid={testId}>
            <div className="text-[9px] font-bold uppercase tracking-widest opacity-80">{label}</div>
            <div className="mt-0.5 flex items-baseline gap-1 text-base font-bold">
                <span>{inr(value)}</span>
                {suffix && <span className="text-[10px] font-black opacity-70">{suffix}</span>}
            </div>
        </div>
    );
}
